/* ============================================================
 * Contact Tabs — Tab-Umschaltung + sessionStorage-Befüllung
 *
 * Lebenszyklus pro <section class="contact-tabs">:
 *   1. Tab-Buttons (data-target="…") schalten Panels um und
 *      merken den letzten aktiven Tab in sessionStorage.
 *   2. Beim Laden wird sessionStorage["pcw-kalkulation"]
 *      gelesen (geschrieben vom Preisrechner-Wizard) und in
 *      die richtigen Felder eingetragen – plus Tab-Auswahl
 *      basierend auf state.kundentyp.
 * ============================================================ */

(function () {
  'use strict';

  const STORAGE_KEY_PCW = 'pcw-kalkulation';
  const STORAGE_KEY_PREFILL = 'contact-prefill';
  const STORAGE_KEY_TAB = 'contact-tabs:active';
  const PREFILL_MAX_AGE_MS = 15 * 60 * 1000;

  function $(sel, scope) {
    return (scope || document).querySelector(sel);
  }
  function $$(sel, scope) {
    return Array.from((scope || document).querySelectorAll(sel));
  }

  function setValue(el, value) {
    if (!el || value == null || value === '') return;
    // Nur überschreiben, wenn das Feld leer ist (User-Eingaben respektieren).
    if (el.value && el.value.trim() !== '') return;
    el.value = value;
    el.dispatchEvent(new Event('input', { bubbles: true }));
  }

  function readPcwData() {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY_PCW);
      if (!raw) return null;
      return JSON.parse(raw);
    } catch (_) {
      return null;
    }
  }

  // Generic handoff for any "Anfragen" link elsewhere on the shop (e.g. the
  // coming-soon buy-button replacement on meter products) — just a message,
  // no positions/pricing like the price calculator's payload.
  function readPrefillData() {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY_PREFILL);
      if (!raw) return null;

      const data = JSON.parse(raw);
      // This script only ships with the contact-tabs section, and the link
      // target is merchant-editable — pointing it at a page without the section
      // would leave the payload in sessionStorage for the rest of the session
      // and prefill a stale message on a later, unrelated visit. Expire by age.
      const age = typeof data?.ts === 'number' ? Date.now() - data.ts : Infinity;
      if (age > PREFILL_MAX_AGE_MS) {
        sessionStorage.removeItem(STORAGE_KEY_PREFILL);
        return null;
      }
      return data;
    } catch (_) {
      return null;
    }
  }

  function applyPrefillMessage(root, data) {
    if (!data || !data.message) return;

    // Unlike the price calculator payload this handoff carries no customer
    // type, so the tab selection stays untouched (activating one would persist
    // the wrong tab into STORAGE_KEY_TAB) and the visible panel gets the text.
    const targetPanel = data.customerType === 'business' ? 'business' : null;
    if (targetPanel) activateTab(root, targetPanel);

    // No customer type → fill every panel, so switching tabs keeps the message.
    const panels = targetPanel
      ? $$(`.contact-tabs__panel[data-panel="${targetPanel}"]`, root)
      : $$('.contact-tabs__panel', root);
    panels.forEach((panel) => {
      setValue($('textarea[name="contact[body]"]', panel), data.message);
    });
  }

  function buildMessageFromPositionen(data) {
    if (!data || !Array.isArray(data.positionen) || data.positionen.length === 0) {
      return '';
    }
    const lines = ['Anfrage aus dem Preisrechner:', ''];

    // Eckdaten aus dem geführten Assistenten (Schritt 1). Feldnamen synchron zum
    // Producer in price-calculator-wizard.js → angebotAnfordern().
    const eckdaten = [
      ['Anzahl Immobilien', data.immoCount],
      ['Wohneinheiten gesamt', data.gesamtWE],
      ['Benötigte Datensammler', data.gatewayMenge],
    ].filter(([, value]) => value != null && value !== '');

    // Aufschlüsselung je Immobilie: Wohneinheiten + empfohlener Gerätetyp (Label
    // kommt fertig vom Wizard, damit die Editor-Bezeichnungen nicht divergieren).
    const immobilien = Array.isArray(data.properties) ? data.properties : [];

    if (eckdaten.length || immobilien.length) {
      lines.push('Ihre Angaben:');
      eckdaten.forEach(([label, value]) => lines.push(`- ${label}: ${value}`));
      immobilien.forEach((p, i) => {
        const teile = [`${p.we != null && p.we !== '' ? p.we : '?'} WE`];
        if (p.geraetLabel) teile.push(p.geraetLabel);
        lines.push(`- Immobilie ${i + 1}: ${teile.join(' · ')}`);
      });
      lines.push('');
    }

    lines.push('Kalkulierte Positionen:');
    data.positionen.forEach((p) => {
      const betrag = typeof p.betrag === 'number'
        ? p.betrag.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })
        : p.betrag;
      lines.push(`- ${p.label}: ${betrag} (${p.periode || '–'})`);
    });
    lines.push('', 'Bitte erstellen Sie uns ein passendes Angebot.');
    return lines.join('\n');
  }

  function activateTab(root, target) {
    const buttons = $$('.contact-tabs__tab-btn', root);
    const panels = $$('.contact-tabs__panel', root);

    buttons.forEach((b) => {
      const isActive = b.dataset.target === target;
      b.classList.toggle('is-active', isActive);
      b.setAttribute('aria-selected', isActive ? 'true' : 'false');
      b.tabIndex = isActive ? 0 : -1;
    });

    panels.forEach((p) => {
      const isActive = p.dataset.panel === target;
      p.classList.toggle('is-hidden', !isActive);
      p.setAttribute('aria-hidden', isActive ? 'false' : 'true');
    });

    try {
      sessionStorage.setItem(STORAGE_KEY_TAB, target);
    } catch (_) { /* ignore */ }
  }

  function setupTabs(root) {
    const buttons = $$('.contact-tabs__tab-btn', root);
    if (buttons.length === 0) return;

    buttons.forEach((btn) => {
      btn.addEventListener('click', () => activateTab(root, btn.dataset.target));
    });

    // Initialer Zustand: gemerkter Tab > erster Tab
    let initial = null;
    try {
      initial = sessionStorage.getItem(STORAGE_KEY_TAB);
    } catch (_) { /* ignore */ }

    const validTargets = buttons.map((b) => b.dataset.target);
    if (!initial || !validTargets.includes(initial)) {
      initial = validTargets[0];
    }
    activateTab(root, initial);
  }

  function applyPcwData(root, pcwData) {
    if (!pcwData) return;

    // Customer type is optional: the price calculator dropped its
    // private/business question, so a payload without it must not silently land
    // in the private tab. Only an explicit type still preselects a tab —
    // otherwise the remembered/first tab stays active and the customer chooses.
    let targetPanel = null;
    if (pcwData.kundentyp === 'business' || pcwData.kundentyp === 'geschaeft') {
      targetPanel = 'business';
    } else if (pcwData.kundentyp === 'private' || pcwData.kundentyp === 'privat') {
      targetPanel = 'private';
    }
    if (targetPanel) activateTab(root, targetPanel);

    // Without a known customer type every panel gets the data, so the form is
    // ready whichever tab the customer switches to.
    const panels = targetPanel
      ? $$(`.contact-tabs__panel[data-panel="${targetPanel}"]`, root)
      : $$('.contact-tabs__panel', root);
    if (panels.length === 0) return;

    const fullName = [pcwData.vorname, pcwData.nachname].filter(Boolean).join(' ').trim();
    // The wizard ships the finished text along (renderOfferText) so that contact
    // field, lead mail and offer PDF cannot drift apart. The local text builder
    // stays as the fallback for other producers and older, still-open tabs.
    const message = typeof pcwData.message === 'string' && pcwData.message
      ? pcwData.message
      : buildMessageFromPositionen(pcwData);

    panels.forEach((panel) => {
      setValue($('input[name="contact[name]"]', panel), fullName);
      setValue($('input[name="contact[first_name]"]', panel), pcwData.vorname);
      setValue($('input[name="contact[last_name]"]', panel), pcwData.nachname);
      setValue($('input[name="contact[email]"]', panel), pcwData.email);
      setValue($('textarea[name="contact[body]"]', panel), message);
    });
  }


  let handlersBound = false;
  function bindFormHandlers() {
    if (handlersBound) return;
    handlersBound = true;

    // `invalid` bubblet nicht → Capture-Phase.
    document.addEventListener(
      'invalid',
      (event) => {
        const form = event.target && event.target.closest
          ? event.target.closest('.contact-tabs__form')
          : null;
        if (form) form.classList.add('is-validated');
      },
      true
    );
  }

  /**
   * Reload-Pfad (z. B. nach hCaptcha): Liquid rendert die Erfolgsbox
   * serverseitig sichtbar (form.posted_successfully?). Den dazugehörigen
   * Tab aktiv schalten, damit die Bestätigung im sichtbaren Panel liegt.
   */
  function activatePanelWithVisibleSuccess(root) {
    const visibleSuccess = $('[data-success]:not([hidden])', root);
    if (!visibleSuccess) return;
    const panel = visibleSuccess.closest('.contact-tabs__panel');
    if (panel && panel.dataset.panel) activateTab(root, panel.dataset.panel);
  }

  function focusOnSuccess(root) {
    // Nach dem Reload (?contact_posted=true) rendert Liquid die Erfolgsbox
    // sichtbar. Dorthin scrollen + Fokus setzen, damit die Bestätigung
    // (auch für Screenreader) wahrgenommen wird.
    const notice = $('.contact-tabs__notice--success:not([hidden])', root);
    if (notice) {
      notice.setAttribute('tabindex', '-1');
      notice.focus({ preventScroll: false });
      notice.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  function init() {
    // Submit-/Validierungs-Handler einmal global (delegiert) binden –
    // unabhängig davon, wie oft Sections neu gerendert werden.
    bindFormHandlers();

    $$('.contact-tabs').forEach((root) => {
      setupTabs(root);

      const pcw = readPcwData();
      if (pcw) {
        applyPcwData(root, pcw);
        // Daten wurden konsumiert → entfernen, damit sie nach Reload nicht erneut greifen
        try { sessionStorage.removeItem(STORAGE_KEY_PCW); } catch (_) { /* ignore */ }
      }

      const prefill = readPrefillData();
      if (prefill) {
        applyPrefillMessage(root, prefill);
        try { sessionStorage.removeItem(STORAGE_KEY_PREFILL); } catch (_) { /* ignore */ }
      }

      activatePanelWithVisibleSuccess(root);
      focusOnSuccess(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
